import * as React from 'react';
import axios from '@/lib/v2/axios';
import { getUnitOwners, removeUserFromUnit } from '@/api/units';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  CircularProgress,
  DialogContent,
  DialogContentText,
  Divider,
  IconButton,
  lighten,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  MenuList,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  DotsThree as DotsThreeIcon,
  Pencil as PencilIcon,
  Trash as TrashIcon,
  UserPlus as UserPlusIcon,
  UsersThree as UsersThreeIcon,
} from '@phosphor-icons/react/dist/ssr';
import { useQuery, useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useConfirm } from 'material-ui-confirm';

import { type User } from '@/types/v2/user';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { RouterLink } from '@/components/router-link';
import { ProfessionalDialog, DialogActionButtons } from '@/components/common';

interface OwnersTableProps {
  unitId: string;
}

interface OwnersResponse {
  owners: User[];
}

export function OwnersTable({ unitId }: OwnersTableProps): React.JSX.Element {
  const { community } = useUser();
  const addUserDialog = useDialog();
  const [removing, setRemoving] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | { element: HTMLElement; rowId: string }>(null);
  const open = Boolean(anchorEl);
  const confirm = useConfirm();

  const { data, isLoading, refetch } = useQuery<OwnersResponse>({
    queryKey: ['owners', unitId],
    queryFn: () =>
      getUnitOwners(unitId, {
        sort: '-move_out_date',
        current_owner: true,
      }),
  });

  const owners = data?.owners || [];

  // Fetch unit details for the confirmation dialog
  const { data: unitData } = useQuery({
    queryKey: ['unit', unitId],
    queryFn: async () => {
      const response = await axios.get(`/api/v2/units/${unitId}`);
      return response.data;
    },
    enabled: Boolean(unitId),
  });

  const unit = unitData?.unit || unitData;

  // Fetch community users for dropdown (excluding account admins)
  const { data: usersData, isLoading: usersLoading } = useQuery<{ residents?: User[] } | User[]>({
    queryKey: ['community-residents', community?.id],
    queryFn: async () => {
      const response = await axios.get(`/api/v2/communities/${community?.id}/residents`, {
        params: { all: 1 },
      });
      return response.data;
    },
    enabled: Boolean(community?.id),
  });

  const allUsers: User[] = React.useMemo(() => {
    if (!usersData) return [] as User[];
    
    let users: User[] = [];
    if (Array.isArray(usersData)) {
      users = usersData;
    } else if ((usersData as any).residents && Array.isArray((usersData as any).residents)) {
      users = (usersData as any).residents as User[];
    }
    
    // Filter out account admins and users already assigned to this unit
    return users.filter(user => {
      // Filter out account admins
      if (user.account_roles && Array.isArray(user.account_roles)) {
        const hasAccountAdmin = user.account_roles.some(role => 
          role.name === 'account_owner' || 
          role.name === 'account_admin' || 
          role.name === 'account_manager'
        );
        if (hasAccountAdmin) return false;
      }
      
      // Filter out users already assigned to this unit
      const isAlreadyAssigned = owners.some(owner => owner.id === user.id);
      if (isAlreadyAssigned) return false;
      
      return true;
    });
  }, [usersData, owners]);

  const [selectedUserId, setSelectedUserId] = React.useState<string>('');

  const assignUserMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUserId) throw new Error('Please select a user');
      const response = await axios.post(`/api/v2/units/${unitId}/assign-user`, {
        user_id: selectedUserId,
        role_name: 'unit_resident',
      });
      return response.data;
    },
    onSuccess: async (data) => {
      setSelectedUserId('');
      addUserDialog.handleClose();
      
      // Show appropriate message based on whether user was already assigned
      if (data?.already_assigned) {
        toast.success('User is already assigned to this unit');
      } else {
        toast.success('User assigned to unit successfully');
      }
      
      await refetch();
    },
  });

  if (!unitId) {
    return <div>Unit ID is required</div>;
  }

  const columns = React.useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 150,
        accessorFn: (row) => (
          <Link component={RouterLink} href={paths.users.details(row.id)}>
            {`${row.first_name} ${row.last_name}`}
          </Link>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 150,
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        size: 150,
        accessorFn: (row) => row?.phone || 'N/A',
      },
      {
        accessorKey: 'move_in_date',
        header: 'Moved In Date',
        size: 150,
        accessorFn: (row) => (row?.pivot?.move_in_date ? dayjs(row?.pivot?.move_in_date).format('MMM DD, YYYY') : 'N/A'),
      },
    ],
    []
  );

  const handleRemove = React.useCallback(
    async (row: User) => {
      confirm({
        title: 'Remove Resident from Unit',
        content: (
          <Box sx={{ p: 2 }}>
            {/* User Info Section */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: 3,
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Avatar 
                sx={{ 
                  width: 48, 
                  height: 48, 
                  bgcolor: 'primary.main',
                  fontSize: '1.25rem'
                }}
              >
                {row.first_name?.[0] || row.last_name?.[0] || row.email?.[0] || 'U'}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }} variant="h6">
                  {row.first_name && row.last_name 
                    ? `${row.first_name} ${row.last_name}`
                    : row.first_name || row.last_name || 'No Name'
                  }
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 0.5 }} variant="body2">
                  {row.email}
                </Typography>
                {row.phone ? <Typography color="text.secondary" variant="body2">
                    {row.phone}
                  </Typography> : null}
              </Box>
            </Box>

            {/* Unit Info Section */}
            <Box sx={{ 
              mb: 3,
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'warning.main'
            }}>
              <Typography sx={{ fontWeight: 600, color: 'warning.main', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }} variant="subtitle1">
                <Box component="span" sx={{ 
                  width: 20, 
                  height: 20, 
                  borderRadius: '50%', 
                  backgroundColor: 'warning.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  ⚠️
                </Box>
                Unit Assignment Details
              </Typography>
              {unit ? <>
                  <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
                    <strong>Unit:</strong> {unit.unit_number ? `Unit #${unit.unit_number}` : 'No Unit Number'}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
                    <strong>Address:</strong> {unit.address}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
                    <strong>Location:</strong> {unit.city}, {unit.state} {unit.zip_code}
                  </Typography>
                </> : null}
              <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
                <strong>Unit ID:</strong> {unitId}
              </Typography>
              {row.pivot?.move_in_date ? <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
                  <strong>Moved In:</strong> {dayjs(row.pivot.move_in_date).format('MMM DD, YYYY')}
                </Typography> : null}

            </Box>

            {/* Warning Message */}
            <Box sx={{ 
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'error.main'
            }}>
              <Typography sx={{ fontWeight: 600, color: 'error.main', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }} variant="subtitle1">
                <Box component="span" sx={{ 
                  width: 20, 
                  height: 20, 
                  borderRadius: '50%', 
                  backgroundColor: 'error.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  🚨
                </Box>
                Important Information
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
                This action will <strong>permanently remove</strong> {row.first_name || 'this resident'} from the unit.
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 1 }} variant="body2">
                • All unit-specific roles and permissions will be lost
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 1 }} variant="body2">
                • The resident will no longer have access to unit-related features
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 1 }} variant="body2">
                • This action cannot be undone
              </Typography>
            </Box>

            {/* Confirmation Text */}
            <Box sx={{ mt: 3, textAlign: 'center', p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontWeight: 500, color: 'text.primary', mb: 1 }} variant="body1">
                Please confirm your action
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Are you sure you want to remove this resident from the unit?
              </Typography>
            </Box>
          </Box>
        ),
        dialogProps: { 
          maxWidth: 'md',
          sx: { 
            '& .MuiDialog-paper': { 
              borderRadius: 3,
              minWidth: 500
            } 
          }
        },
        confirmationText: 'Remove Resident',
        cancellationText: 'Cancel',
        buttonOrder: ['confirm', 'cancel'],
        confirmationButtonProps: {
          variant: 'contained',
          color: 'error',
          sx: { 
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }
        },
        cancellationButtonProps: {
          variant: 'outlined',
          sx: { 
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }
        }
      })
        .then(async () => {
          setRemoving(true);
          try {
            // Use the simple delete endpoint instead of move-out
            await removeUserFromUnit(row.id, unitId);
            toast.success('Resident removed successfully');
            refetch();
          } catch (error: any) {
            console.error('Error removing resident:', error);
            toast.error(error?.response?.data?.message || 'Failed to remove resident');
          } finally {
            setRemoving(false);
            setAnchorEl(null);
          }
        })
        .catch(() => { setRemoving(false); });
    },
    [unitId, refetch, confirm]
  );

  const table = useMaterialReactTable<User>({
    columns,
    data: owners || [],
    initialState: {
      showGlobalFilter: true,
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
    },
    muiSearchTextFieldProps: { size: 'small', variant: 'outlined' },
    state: { isLoading },
    enableRowActions: true,

    positionActionsColumn: 'last',
    muiTableContainerProps: (props) => ({
      ...props,
      sx: { minHeight: '200px' },
    }),
    renderRowActions: ({ row }) => (
      <React.Fragment>
        <IconButton
          onClick={(event) => {
            setAnchorEl({ element: event.currentTarget, rowId: row.id });
          }}
          size="small"
        >
          <DotsThreeIcon size={20} />
        </IconButton>
        <Menu
          anchorEl={anchorEl?.element}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={() => { setAnchorEl(null); }}
          open={open ? anchorEl?.rowId === row.id : null}
          sx={{ minWidth: 300 }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuList>
            <MenuItem onClick={() => handleRemove(row.original)}>
              <ListItemIcon>
                <TrashIcon />
              </ListItemIcon>
              {removing ? 'Removing...' : 'Remove'}
            </MenuItem>
          </MenuList>
        </Menu>
      </React.Fragment>
    ),
    renderTopToolbar: ({ table }) => {
      return (
        <Box
          sx={(theme) => ({
            backgroundColor: lighten(theme.palette.background.default, 0.05),
            display: 'flex',
            gap: '0.5rem',
            p: '8px',
            justifyContent: 'space-between',
          })}
        >
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {/* import MRT sub-components */}
            <MRT_GlobalFilterTextField table={table} />
            <MRT_ToggleFiltersButton table={table} />
          </Box>
        </Box>
      );
    },
  });

  return (
    <React.Fragment>
      <Card>
        <CardHeader
          avatar={
            <Avatar>
              <UsersThreeIcon fontSize="var(--Icon-fontSize)" />
            </Avatar>
          }
          subheader="Current residents assigned to this unit."
          title={
            <Box alignItems="center" display="flex" gap={2} justifyContent="space-between">
              <Typography variant="h6">Residents</Typography>
              <Box display="flex" gap={2}>
                <Button onClick={addUserDialog.handleOpen} size="small" startIcon={<UserPlusIcon size={20} />} variant="outlined">
                  Add User
                </Button>
              </Box>
            </Box>
          }
        />
        <Divider />
        <MaterialReactTable table={table} />
      </Card>

      {/* Add User Dialog */}
      <ProfessionalDialog
        actions={
          <DialogActionButtons
            confirmText={assignUserMutation.isPending ? 'Adding...' : 'Add User'}
            disabled={!selectedUserId}
            loading={assignUserMutation.isPending}
            onCancel={() => {
              setSelectedUserId('');
              addUserDialog.handleClose();
            }}
            onConfirm={() => { assignUserMutation.mutate(); }}
          />
        }
        autoHeight
        contentSx={{ pt: 2, px: 3, pb: 2 }}
        icon={<UserPlusIcon size={20} />}
        maxWidth="sm"
        onClose={() => {
          setSelectedUserId('');
          addUserDialog.handleClose();
        }}
        open={addUserDialog.open}
        subtitle="Select a user in this community to add as a resident"
        title="Add User to Unit"
      >
        <Box>
          <Autocomplete
            blurOnSelect
            clearOnBlur={false}
            disabled={usersLoading}
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter(option => {
                const name = `${option.first_name ?? ''} ${option.last_name ?? ''}`.toLowerCase();
                const email = (option.email ?? '').toLowerCase();
                return name.includes(searchTerm) || email.includes(searchTerm);
              });
            }}
            getOptionLabel={(option) => {
              const name = `${option.first_name ?? ''} ${option.last_name ?? ''}`.trim();
              return name || option.email || '';
            }}
            loading={usersLoading}
            noOptionsText="No users found"
            onChange={(event, newValue) => {
              setSelectedUserId(newValue?.id || '');
            }}
            options={allUsers}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                label="Search and select user"
                placeholder="Type to search users..."
              />
            )}
            renderOption={(props, option) => {
              const name = `${option.first_name ?? ''} ${option.last_name ?? ''}`.trim();
              const email = option.email ?? '';
              const initials = `${option.first_name?.[0] ?? ''}${option.last_name?.[0] ?? ''}`.toUpperCase();
              
              return (
                <Box component="li" {...props} sx={{ py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
                      {initials || email?.[0] || '?'}
                    </Avatar>
                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <Typography noWrap sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.25 }} variant="body2">
                        {name || email}
                      </Typography>
                      <Typography color="text.secondary" noWrap variant="caption">
                        {email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            }}
            value={allUsers.find(u => u.id === selectedUserId) || null}
          />
        </Box>
      </ProfessionalDialog>
    </React.Fragment>
  );
}
